import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleOrders: React.FC = () => {
  const BASE = '/images/help/orders';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/ord-step-1.png`, alt: 'Orders dashboard overview', caption: 'Step 1 – The Orders dashboard with all your eBay sales in one place' },
    ],
    step2: [
      { src: `${BASE}/ord-step-2-1.png`, alt: 'Order details view', caption: 'Step 2-1 – Order details: buyer address, product, ASIN, and profit' },
      { src: `${BASE}/ord-step-2-2.png`, alt: 'Tracking entry and dispatch confirmation', caption: 'Step 2-2 – Tracking entry and dispatch confirmation' },
    ],
    step7: [
      { src: `${BASE}/ord-ao-1.png`, alt: 'AO queue view', caption: 'AO – Queue view showing pending automatic orders' },
      { src: `${BASE}/ord-ao-2.png`, alt: 'AO checkout in progress', caption: 'AO – Checkout flow in progress on Amazon' },
      { src: `${BASE}/ord-ao-3.png`, alt: 'AO settings panel', caption: 'AO – Settings panel for configuring automatic ordering' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Order Management & Fulfilment | CodeRom Help"
        description="Manage and automate Amazon to eBay order fulfilment with CodeRom."
      />
      <HelpBreadcrumbSchema slug="orders" name="Order management & fulfilment" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Order management & fulfilment</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Order management & fulfilment</h1>
                <p className="article-subtitle">
                  Manage your eBay orders from one place. See new orders, track fulfilment status, and handle cancellations and returns — all from the Coderom dashboard.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 7 min read</span>
                  <span className="category">Orders</span>
                </div>
              </div>

              {/* Hero Image */}
              <img
                src="/images/help/orders/ord-hero.png"
                alt="Coderom Orders dashboard"
                style={{ width: '100%', borderRadius: '12px', marginBottom: '32px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
              />

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. Orders dashboard overview</a></li>
                  <li><a href="#step2">2. Processing a new order</a></li>
                  <li><a href="#step3">3. Order statuses explained</a></li>
                  <li><a href="#step4">4. Handling cancellations</a></li>
                  <li><a href="#step5">5. Returns and refunds</a></li>
                  <li><a href="#step6">6. Order settings</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Orders dashboard overview</h2>
                  <p>
                    Go to <strong>Orders</strong> from the sidebar to see all your eBay orders in one place. The dashboard shows:
                  </p>
                  <ul>
                    <li><strong>New orders</strong> waiting to be fulfilled</li>
                    <li><strong>In progress</strong> orders being processed</li>
                    <li><strong>Completed</strong> orders with tracking uploaded</li>
                    <li><strong>Cancelled</strong> and returned orders</li>
                  </ul>
                  <p>
                    Each order shows the buyer name, product, sale price, profit, and current status.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: Orders dashboard overview" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Orders are synced from eBay automatically. When a buyer purchases your listing, the order appears in Coderom within minutes.
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Processing a new order</h2>
                  <p>
                    When a new order comes in:
                  </p>
                  <ol>
                    <li>Go to <strong>Orders</strong> and click on the new order</li>
                    <li>You'll see the order details: buyer address, product, Amazon ASIN, and calculated profit</li>
                    <li>Purchase the product on Amazon using the buyer's delivery address</li>
                    <li>Once shipped, enter the Amazon Order ID and tracking number in Coderom</li>
                    <li>Coderom uploads the tracking to eBay and marks the order as shipped</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Processing a new order" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> If you have Automatic Ordering (AO) enabled, steps 3-4 are done automatically — see the next article for details.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Order statuses explained</h2>

                  <div className="status-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Meaning</th>
                          <th>Action needed</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>New</strong></td>
                          <td>Buyer has paid, waiting for fulfilment</td>
                          <td>Purchase on Amazon</td>
                        </tr>
                        <tr>
                          <td><strong>Awaiting Shipment</strong></td>
                          <td>Purchased on Amazon, waiting for dispatch</td>
                          <td>Wait for Amazon to ship</td>
                        </tr>
                        <tr>
                          <td><strong>Shipped</strong></td>
                          <td>Tracking uploaded to eBay</td>
                          <td>None — order is complete</td>
                        </tr>
                        <tr>
                          <td><strong>Delivered</strong></td>
                          <td>Buyer has received the item</td>
                          <td>None</td>
                        </tr>
                        <tr>
                          <td><strong>Cancelled</strong></td>
                          <td>Order was cancelled</td>
                          <td>Check reason, process refund if needed</td>
                        </tr>
                        <tr>
                          <td><strong>Return Requested</strong></td>
                          <td>Buyer wants to return the item</td>
                          <td>Handle return</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Handling cancellations</h2>
                  <p>
                    If a buyer requests a cancellation:
                  </p>
                  <ol>
                    <li>Go to the order in <strong>Orders</strong></li>
                    <li>If you haven't purchased on Amazon yet — click <strong>Cancel Order</strong> and process the eBay refund</li>
                    <li>If you've already purchased on Amazon — you'll need to cancel the Amazon order first, then cancel on eBay</li>
                  </ol>

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Respond to cancellation requests quickly. eBay tracks your cancellation rate — a high rate can affect your seller performance.
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Returns and refunds</h2>
                  <p>
                    When a buyer opens a return:
                  </p>
                  <ol>
                    <li>The order status changes to <strong>Return Requested</strong> in Coderom</li>
                    <li>Follow eBay's return process (accept return, provide return label)</li>
                    <li>Once the item is returned to the Amazon address, process the Amazon return</li>
                    <li>Issue the eBay refund through your eBay seller account</li>
                  </ol>
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Order settings</h2>
                  <p>
                    Go to <strong>Settings → Orders</strong> to configure:
                  </p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Auto-sync orders</strong></td>
                          <td>Pull new orders from eBay automatically</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Order notifications</strong></td>
                          <td>Get notified when new orders arrive</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Profit display</strong></td>
                          <td>Show calculated profit on each order</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Default marketplace</strong></td>
                          <td>Which eBay store to show orders from</td>
                          <td>Your primary store</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Step 7 */}
                <div id="step7" className="content-section">
                  <h2>Step 7: Automatic Ordering (AO) <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '12px', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px', fontWeight: 600 }}>Beta</span></h2>
                  <p>Automatic Ordering (AO) is a Chrome Extension feature that automates the Amazon checkout process when you receive an eBay sale. When enabled, Coderom will automatically place the Amazon order on your behalf — entering the buyer's address, completing checkout, and saving the Amazon Order ID for tracking.</p>
                  <div className="tip-box">
                    <span className="tip-icon">🤖</span>
                    <div className="tip-content">
                      <strong>Beta feature:</strong> Automatic Ordering is currently in beta. It requires the Coderom Chrome Extension installed and active in your browser. Enable it in <strong>Settings → Automatic Ordering</strong>.
                    </div>
                  </div>
                  <h3>How AO works with your orders</h3>
                  <ol>
                    <li>An eBay sale comes in and appears in your Orders page</li>
                    <li>Coderom queues the order for automatic processing</li>
                    <li>The Chrome Extension opens Amazon, adds the item to cart, enters the buyer's shipping address, and completes checkout</li>
                    <li>The Amazon Order ID is saved and linked to your eBay order</li>
                    <li>When Amazon dispatches, tracking is automatically uploaded to eBay</li>
                  </ol>
                  <HelpScreenshot images={screenshots.step7} stepLabel="Step 7: Automatic Ordering (AO)" />
                  <h3>AO safety features</h3>
                  <p>AO includes built-in protections to prevent errors:</p>
                  <ul>
                    <li><strong>Price guard</strong> — cancels if Amazon price exceeds your tolerance threshold</li>
                    <li><strong>Address verification</strong> — confirms shipping address before checkout</li>
                    <li><strong>Duplicate prevention</strong> — skips orders already processed</li>
                    <li><strong>Queue timeout</strong> — abandons stuck orders automatically</li>
                  </ul>
                  <div className="tip-box">
                    <span className="tip-icon">⚙️</span>
                    <div className="tip-content">
                      <strong>Configure AO:</strong> Go to <strong>Settings → Automatic Ordering</strong> to set price tolerance, auto-retry, order delay, and notification preferences.
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that you understand order management, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/safety-protection" className="next-step-card">
                      <div className="next-step-icon">🛡️</div>
                      <div className="next-step-content">
                        <h4>Safety & Protection</h4>
                        <p>Protect your store from VeRO claims with blocked brands and keywords</p>
                      </div>
                    </Link>
                    <Link to="/help/gmail-tracking" className="next-step-card">
                      <div className="next-step-icon">📧</div>
                      <div className="next-step-content">
                        <h4>Gmail integration & tracking</h4>
                        <p>Automatically capture Amazon tracking numbers from your Gmail inbox</p>
                      </div>
                    </Link>
                    <Link to="/help/auto-messages" className="next-step-card">
                      <div className="next-step-icon">💬</div>
                      <div className="next-step-content">
                        <h4>Automated buyer messages</h4>
                        <p>Send order confirmations and delivery updates automatically</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .status-table,
        .settings-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .status-table table,
        .settings-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .status-table th,
        .settings-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .status-table td,
        .settings-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .status-table tbody tr:last-child td,
        .settings-table tbody tr:last-child td {
          border-bottom: none;
        }
        .status-table tbody tr:hover,
        .settings-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .status-table,
          .settings-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleOrders;
